import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Upload, CheckCircle, Shirt, Paintbrush, Phone, ShoppingBag, Home } from "lucide-react";
import { API, GARMENT_TYPES, PRINT_LOCATIONS } from "../constants";
import { ArtworkQualityAlert } from "./ArtworkQualityAlert";
import { SEO } from "../components/SEO";

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [sizeBreakdown, setSizeBreakdown] = useState({ S: 0, M: 0, L: 0, XL: 0, "2XL": 0, "3XL": 0 });
  const [dismissedNotifications, setDismissedNotifications] = useState({});
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchCurrentX, setTouchCurrentX] = useState(0);
  
  // Notification dismissal functions
  const dismissNotification = (notificationId) => {
    setDismissedNotifications(prev => ({
      ...prev,
      [notificationId]: true
    }));
  };

  const handleTouchStart = (e, notificationId) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchCurrentX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e, notificationId) => {
    const swipeDistance = touchStartX - touchCurrentX;
    // If swiped more than 100px to the right, dismiss notification
    if (swipeDistance > 100) {
      dismissNotification(notificationId);
    }
    setTouchStartX(0);
    setTouchCurrentX(0);
  };
  
  // Function to get minimum quantity for each product
  const getMinimumQuantity = (productId) => {
    switch (productId) {
      case 'stickers': return 25;
      case 'embroidery': return 2;
      case 'fastisol_transfers': return 5;
      default: return 1;
    }
  };

  // Function to get next price break notification
  const getPriceBreakInfo = (productId, currentQuantity) => {
    if (!product) return null;
    
    let nextBreak = null;
    
    if (productId === 'screen_printing' && product.quantity_pricing) {
      // Find the next quantity break for screen printing
      for (let tier of product.quantity_pricing) {
        if (currentQuantity < tier.min) {
          nextBreak = {
            quantity: tier.min,
            away: tier.min - currentQuantity,
            benefit: 'Lower per-piece pricing + potential setup fee removal'
          };
          break;
        }
      }
    } else if (productId === 'embroidery' && product.quantity_multipliers && !isNamesOnly) {
      // Find the next quantity break for embroidery (not applicable to names only)
      for (let tier of product.quantity_multipliers) {
        if (currentQuantity < tier.min) {
          nextBreak = {
            quantity: tier.min,
            away: tier.min - currentQuantity,
            benefit: `${Math.round((1 - tier.multiplier) * 100)}% discount per piece`
          };
          break;
        }
      }
    } else if (productId === 'stickers' && product.quantity_breaks) {
      // Find the next quantity break for stickers
      for (let tier of product.quantity_breaks) {
        if (currentQuantity < tier.min) {
          nextBreak = {
            quantity: tier.min,
            away: tier.min - currentQuantity,
            benefit: `${Math.round((1 - tier.multiplier) * 100)}% discount per sticker`
          };
          break;
        }
      }
    } else if (productId === 'dtf_printing' && product.location_pricing) {
      // DTF uses quantity tiers within location pricing
      const frontTiers = product.location_pricing.front || [];
      for (let tier of frontTiers) {
        if (currentQuantity < tier.min) {
          nextBreak = {
            quantity: tier.min,
            away: tier.min - currentQuantity,
            benefit: 'Lower per-piece pricing'
          };
          break;
        }
      }
    }
    
    return nextBreak;
  };
  const [garmentType, setGarmentType] = useState('tshirt');
  const [printLocations, setPrintLocations] = useState([]);
  const [colors, setColors] = useState(1);
  const [embroideryWidth, setEmbroideryWidth] = useState(2.0);
  const [embroideryHeight, setEmbroideryHeight] = useState(1.0);
  const [isNamesOnly, setIsNamesOnly] = useState(false);
  const [numberOfNames, setNumberOfNames] = useState(1);
  const [namesList, setNamesList] = useState(['']);
  const [stickerWidth, setStickerWidth] = useState(2.0);
  const [stickerHeight, setStickerHeight] = useState(2.0);
  const [cutType, setCutType] = useState('die_cut');
  const [printWidth, setPrintWidth] = useState(3.0);
  const [printHeight, setPrintHeight] = useState(2.0);
  const [printCategory, setPrintCategory] = useState('vinyl_banners');
  const [printProduct, setPrintProduct] = useState('13oz_vinyl_banner');
  const [garmentColor, setGarmentColor] = useState('light'); // light or dark
  const [detectedColors, setDetectedColors] = useState(1);
  const [price, setPrice] = useState(0);
  const [priceBreakdown, setPriceBreakdown] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [qualityAnalysis, setQualityAnalysis] = useState(null);
  const [artFeeAccepted, setArtFeeAccepted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Helper functions for names management (after state declarations)
  const updateNamesList = (index, value) => {
    const newNamesList = [...namesList];
    newNamesList[index] = value;
    setNamesList(newNamesList);
  };

  const removeName = (index) => {
    if (numberOfNames > 1) {
      const newNamesList = namesList.filter((_, i) => i !== index);
      setNamesList(newNamesList);
      setNumberOfNames(prev => prev - 1);
    }
  };

  // Sync numberOfNames with namesList length
  useEffect(() => {
    setNamesList(prev => {
      if (numberOfNames > prev.length) {
        const newNamesList = [...prev];
        while (newNamesList.length < numberOfNames) {
          newNamesList.push('');
        }
        return newNamesList;
      } else if (numberOfNames < prev.length) {
        return prev.slice(0, numberOfNames);
      }
      return prev;
    });
  }, [numberOfNames]); // eslint-disable-line

  useEffect(() => {
    fetchProduct();
    
    // Set default print location for screen printing
    if (productId === 'screen_printing') {
      setPrintLocations(['front']);
    } else {
      setPrintLocations([]);
    }
    
    // Auto-set minimum quantity for each product
    const minQuantity = getMinimumQuantity(productId);
    setQuantity(minQuantity);
  }, [productId]); // eslint-disable-line

  useEffect(() => {
    // Set default print locations based on product type
    if (productId === 'screen_printing') {
      setPrintLocations(['front']);
    } else {
      setPrintLocations([]);
    }
  }, [productId]); // eslint-disable-line

  useEffect(() => {
    if (product && quantity > 0) {
      // Check if product is ready for pricing based on type
      let shouldCalculatePrice = false;
      
      if (productId === 'dtf_printing') {
        // DTF only needs garment type
        shouldCalculatePrice = !!garmentType;
      } else if (productId === 'screen_printing') {
        // Screen printing needs garment type and print locations
        shouldCalculatePrice = !!garmentType && printLocations.length > 0;
      } else if (productId === 'embroidery') {
        // Embroidery needs garment type and either size dimensions OR names-only mode
        shouldCalculatePrice = !!garmentType && ((embroideryWidth > 0 && embroideryHeight > 0) || isNamesOnly);
      } else if (productId === 'stickers') {
        // Stickers need dimensions and cut type
        shouldCalculatePrice = stickerWidth > 0 && stickerHeight > 0 && !!cutType;
      } else if (productId === 'large_format') {
        // Large format needs category and product selection
        shouldCalculatePrice = !!printCategory && !!printProduct;
      }
      
      if (shouldCalculatePrice) {
        calculatePrice();
      } else {
        // Reset price if configuration is incomplete
        setPrice(0);
        setPriceBreakdown({});
      }
    }
  }, [product, quantity, garmentType, printLocations, colors, embroideryWidth, embroideryHeight, isNamesOnly, numberOfNames, namesList, stickerWidth, stickerHeight, cutType, printWidth, printHeight, printCategory, printProduct, garmentColor, detectedColors, artFeeAccepted]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      const productData = response.data.product;
      setProduct(productData);
      
      if (productId === 'fastisol_transfers') {
        setQuantity(5);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    try {
      const params = {
        product_id: productId,
        quantity: quantity
      };

      // Only add garment type for products that need it (not stickers or large format)
      if (!['stickers', 'large_format'].includes(productId)) {
        params.garment_type = garmentType;
      }

      if (productId === 'dtf_printing') {
        // DTF can have optional print locations, default to ["front"] if none selected
        params.print_locations = printLocations.length > 0 ? printLocations : ["front"];
      }
      
      if (productId === 'screen_printing') {
        if (printLocations.length === 0) {
          setPrice(0);
          setPriceBreakdown({});
          return;
        }
        params.print_locations = printLocations;
      }
      
      if (productId === 'screen_printing') {
        // For screen printing, use detected colors or manual selection
        // Add 1 color if dark garment (for underbase)
        const finalColors = garmentColor === 'dark' ? detectedColors + 1 : detectedColors;
        params.colors = finalColors;
        params.garment_color = garmentColor;
      }
      
      if (productId === 'embroidery') {
        params.is_names_only = isNamesOnly;
        if (isNamesOnly) {
          // For names-only, send the number of names and name list
          params.number_of_names = numberOfNames;
          params.names_list = namesList.filter(name => name.trim() !== '');
        } else {
          // For regular embroidery, send size dimensions
          params.embroidery_size_inches = embroideryWidth * embroideryHeight; // Calculate square inches
        }
      }

      if (productId === 'stickers') {
        params.sticker_width = stickerWidth;
        params.sticker_height = stickerHeight;
        params.cut_type = cutType;
      }

      if (productId === 'large_format') {
        params.print_width = printWidth;
        params.print_height = printHeight;
        params.print_category = printCategory;
        params.print_product = printProduct;
      }

      // Handle array parameters correctly for FastAPI
      const requestParams = { ...params };
      if (requestParams.print_locations && Array.isArray(requestParams.print_locations)) {
        delete requestParams.print_locations;
      }
      
      // Create URLSearchParams for proper array handling
      const urlParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (key === 'print_locations' && Array.isArray(value)) {
          value.forEach(location => urlParams.append('print_locations', location));
        } else if (value !== null && value !== undefined) {
          urlParams.append(key, value);
        }
      });
      
      const response = await axios.post(`${API}/calculate-price?${urlParams.toString()}`);
      let totalPrice = response.data.total_price;
      
      // Add art fee if accepted
      if (artFeeAccepted) {
        totalPrice += 25 * quantity; // $25 art fee per piece
      }
      
      setPrice(totalPrice);
      setPriceBreakdown(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
      setPrice(0);
      setPriceBreakdown({});
      if (error.response?.data?.detail) {
        console.warn('Pricing error:', error.response.data.detail);
      }
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-artwork`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setUploadedFile(response.data);
      setQualityAnalysis(response.data.quality_analysis);
      setArtFeeAccepted(false); // Reset art fee acceptance
      
      // Auto-detect colors for screen printing
      if (productId === 'screen_printing' && response.data.quality_analysis.colors) {
        setDetectedColors(Math.min(response.data.quality_analysis.colors, 8));
        toast.success(`Colors detected: ${response.data.quality_analysis.colors}. Pricing updated automatically.`);
      } else if (response.data.quality_analysis.status === 'excellent') {
        toast.success('Perfect! Your artwork is print-ready.');
      } else if (response.data.quality_analysis.needs_art_fee) {
        toast.warning('Artwork needs enhancement - $25 art fee applies.');
      } else {
        toast.success('File uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file. Please check the file format.');
    } finally {
      setUploading(false);
    }
  };

  const handleAcceptArtFee = () => {
    setArtFeeAccepted(true);
    toast.success('Art fee accepted. Price updated.');
  };

  const handleLocationChange = (location, checked) => {
    if (checked) {
      setPrintLocations(prev => [...prev, location]);
    } else {
      setPrintLocations(prev => prev.filter(loc => loc !== location));
    }
  };

  const addToCart = () => {
    const hasSizes = !['stickers', 'large_format', 'fastisol_transfers'].includes(productId);
    const cartItem = {
      id: `${productId}-${Date.now()}`,
      product_id: productId,
      product_name: product.name,
      quantity: quantity,
      size_breakdown: hasSizes ? Object.fromEntries(Object.entries(sizeBreakdown).filter(([_, v]) => v > 0)) : null,
      garment_type: !['stickers', 'large_format'].includes(productId) ? garmentType : null,
      print_locations: printLocations,
      colors: productId === 'screen_printing' || productId === 'stickers' ? colors : null,
      embroidery_size_inches: productId === 'embroidery' ? embroideryWidth * embroideryHeight : null,
      is_names_only: productId === 'embroidery' ? isNamesOnly : null,
      sticker_width: productId === 'stickers' ? stickerWidth : null,
      sticker_height: productId === 'stickers' ? stickerHeight : null,
      cut_type: productId === 'stickers' ? cutType : null,
      print_width: productId === 'large_format' ? printWidth : null,
      print_height: productId === 'large_format' ? printHeight : null,
      print_category: productId === 'large_format' ? printCategory : null,
      print_product: productId === 'large_format' ? printProduct : null,
      price: price,
      filename: uploadedFile?.filename,
      file_path: uploadedFile?.file_path,
      unit_price: price / quantity,
      price_breakdown: priceBreakdown,
      art_fee: artFeeAccepted ? 25 : 0,
      quality_status: qualityAnalysis?.status || 'unknown'
    };

    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    existingCart.push(cartItem);
    localStorage.setItem('cart', JSON.stringify(existingCart));
    
    toast.success('Added to cart!');
    navigate('/cart');
  };

  const requestCall = () => {
    const hasSizes = !['stickers', 'large_format', 'fastisol_transfers'].includes(productId);
    const requestData = {
      product_id: productId,
      product_name: product.name,
      quantity: quantity,
      size_breakdown: hasSizes ? Object.fromEntries(Object.entries(sizeBreakdown).filter(([_, v]) => v > 0)) : null,
      garment_type: !['stickers', 'large_format'].includes(productId) ? garmentType : null,
      print_locations: printLocations,
      colors: colors,
      embroidery_size_inches: embroideryWidth * embroideryHeight,
      is_names_only: isNamesOnly,
      estimated_price: price,
      filename: uploadedFile?.filename,
      art_fee: artFeeAccepted ? 25 : 0,
      quality_analysis: qualityAnalysis
    };
    
    localStorage.setItem('callRequestData', JSON.stringify(requestData));
    navigate('/request-call');
  };

  const renderProductOptions = () => {
    if (!product) return null;

    return (
      <div className="space-y-6">
        {/* Garment Selection - Not for Stickers or Large Format */}
        {!['stickers', 'large_format'].includes(productId) && (
          <div>
            <Label className="text-lg font-semibold">Choose Garment</Label>
            <div className="text-sm text-gray-600 mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="font-medium">📝 Note:</span> These are our standard blank garments. 
              For specific brands or custom garments, please submit a 
              <button 
                onClick={() => setActiveSection('concierge')}
                className="text-blue-600 hover:text-blue-800 underline ml-1 mr-1"
              >
                custom quote request
              </button> 
              through our concierge service.
            </div>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {Object.entries(GARMENT_TYPES)
                .filter(([key, garment]) => productId === 'embroidery' || key !== 'hat') // Only show hats for embroidery
                .map(([key, garment]) => (
                <Button
                  key={key}
                  variant={garmentType === key ? "default" : "outline"}
                  className="flex-col h-auto py-3"
                  onClick={() => setGarmentType(key)}
                  data-testid={`garment-${key}`}
                >
                  <Shirt className="w-5 h-5 mb-1" />
                  <span className="text-xs">{garment.name}</span>
                  <span className="text-xs text-gray-500">${garment.price}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* DIY Transfer Note for Fastisol */}
        {productId === 'fastisol_transfers' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Home className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-800">DIY Heat Transfers</span>
            </div>
            <p className="text-sm text-blue-700">
              These are ready-to-press transfers for home use. Apply them to your own garments with a heat press or household iron.
            </p>
          </div>
        )}

        {/* Print Locations */}
        {(productId === 'dtf_printing' || productId === 'screen_printing') && (
          <div>
            <Label className="text-lg font-semibold">
              Select Print Locations {productId === 'screen_printing' ? '*' : '(Optional)'}
            </Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {Object.entries(PRINT_LOCATIONS).map(([key, location]) => (
                <div 
                  key={key} 
                  className={`flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer ${printLocations.includes(key) ? 'bg-blue-50 border-blue-300' : ''}`}
                  onClick={() => {
                    const isCurrentlySelected = printLocations.includes(key);
                    console.log('Location clicked:', key, 'Currently selected:', isCurrentlySelected);
                    handleLocationChange(key, !isCurrentlySelected);
                  }}
                >
                  <input
                    type="checkbox"
                    id={key}
                    checked={printLocations.includes(key)}
                    onChange={() => {}} // Disable onChange to prevent conflicts
                    data-testid={`location-${key}`}
                    className="w-4 h-4 rounded border border-gray-300 pointer-events-none"
                  />
                  <Label htmlFor={key} className="cursor-pointer flex-1 text-sm pointer-events-none">
                    {location.icon} {location.name}
                  </Label>
                </div>
              ))}
            </div>
            {printLocations.length > 0 && (
              <div className="text-sm text-gray-600 mt-2">
                Selected: {printLocations.map(loc => PRINT_LOCATIONS[loc].name).join(', ')}
              </div>
            )}
            {printLocations.length === 0 && productId === 'screen_printing' && (
              <div className="text-sm text-red-600 mt-2">
                Please select at least one print location
              </div>
            )}
            {printLocations.length === 0 && productId === 'dtf_printing' && (
              <div className="text-sm text-blue-600 mt-2">
                Select location or we'll default to front printing
              </div>
            )}
          </div>
        )}

        {/* Screen Printing Options */}
        {productId === 'screen_printing' && (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="font-semibold text-orange-800 mb-2">Screen Printing Setup</h4>
              <p className="text-sm text-orange-700">
                Upload artwork for automatic color detection, or manually select the number of colors in your design.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-lg font-semibold">Garment Color</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Button
                    variant={garmentColor === 'light' ? "default" : "outline"}
                    onClick={() => setGarmentColor('light')}
                    className="h-auto py-3"
                  >
                    <div className="text-center">
                      <div className="w-4 h-4 bg-white border-2 border-gray-300 rounded mx-auto mb-1"></div>
                      <div className="font-semibold">Light Colors</div>
                      <div className="text-xs">White, Light Gray, etc.</div>
                    </div>
                  </Button>
                  <Button
                    variant={garmentColor === 'dark' ? "default" : "outline"}
                    onClick={() => setGarmentColor('dark')}
                    className="h-auto py-3"
                  >
                    <div className="text-center">
                      <div className="w-4 h-4 bg-gray-800 rounded mx-auto mb-1"></div>
                      <div className="font-semibold">Dark Colors</div>
                      <div className="text-xs">Black, Navy, etc.</div>
                    </div>
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-lg font-semibold">Design Colors</Label>
                <Select value={detectedColors.toString()} onValueChange={(value) => setDetectedColors(parseInt(value))}>
                  <SelectTrigger data-testid="colors-select" className="mt-2">
                    <SelectValue placeholder="Select colors in design" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 8}, (_, i) => i + 1).map(num => (
                      <SelectItem key={num} value={num.toString()}>
                        {num} Color{num > 1 ? 's' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Stickers Options */}
        {productId === 'stickers' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">Custom Stickers</h4>
              <p className="text-sm text-green-700 mb-3">
                High-quality vinyl stickers. Pricing calculated by square inches (width × height). Colors don't affect price.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Width (inches)</Label>
                <Input
                  type="number"
                  min="0.5"
                  max="24"
                  step="0.25"
                  value={stickerWidth}
                  onChange={(e) => setStickerWidth(parseFloat(e.target.value) || 2.0)}
                  className="mt-1"
                  data-testid="sticker-width-input"
                />
              </div>
              <div>
                <Label className="text-sm">Height (inches)</Label>
                <Input
                  type="number"
                  min="0.5"
                  max="24"
                  step="0.25"
                  value={stickerHeight}
                  onChange={(e) => setStickerHeight(parseFloat(e.target.value) || 2.0)}
                  className="mt-1"
                  data-testid="sticker-height-input"
                />
              </div>
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <p>Sticker size: {stickerWidth}" × {stickerHeight}" = {(stickerWidth * stickerHeight).toFixed(2)} sq in</p>
              <p className="text-xs text-green-600">
                💡 Colors don't affect price | Die cut and kiss cut available
              </p>
            </div>

            <div>
              <Label className="text-lg font-semibold">Cut Type</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Button
                  variant={cutType === 'die_cut' ? "default" : "outline"}
                  onClick={() => setCutType('die_cut')}
                  className="h-auto py-3 px-4"
                >
                  <div className="text-center">
                    <div className="font-semibold">Die Cut</div>
                    <div className="text-xs mt-1">Cut to shape of design</div>
                  </div>
                </Button>
                <Button
                  variant={cutType === 'kiss_cut' ? "default" : "outline"}
                  onClick={() => setCutType('kiss_cut')}
                  className="h-auto py-3 px-4"
                >
                  <div className="text-center">
                    <div className="font-semibold">Kiss Cut</div>
                    <div className="text-xs mt-1">Cut sticker only, backing intact</div>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Large Format Printing Options */}
        {productId === 'large_format' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Large Format Printing</h4>
              <p className="text-sm text-blue-700 mb-3">
                Professional banners, displays, and event signage. Banners priced per square foot, displays priced per piece.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-lg font-semibold">Print Category</Label>
                <Select value={printCategory} onValueChange={setPrintCategory}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vinyl_banners">Vinyl Banners</SelectItem>
                    <SelectItem value="banner_stands">Pop-Up Banner Stands</SelectItem>
                    <SelectItem value="step_repeat">Step & Repeat Backdrops</SelectItem>
                    <SelectItem value="pillowcase">Pillowcase Backdrops</SelectItem>
                    <SelectItem value="canopies">Event Canopies</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-lg font-semibold">Product Type</Label>
                <Select value={printProduct} onValueChange={setPrintProduct}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {printCategory === 'vinyl_banners' && (
                      <>
                        <SelectItem value="13oz_vinyl_banner">13oz Vinyl Banner</SelectItem>
                        <SelectItem value="mesh_banner">Mesh Banner</SelectItem>
                      </>
                    )}
                    {printCategory === 'banner_stands' && (
                      <>
                        <SelectItem value="retractable_banner_33x79">33"×79" Retractable Banner</SelectItem>
                        <SelectItem value="retractable_banner_47x79">47"×79" Retractable Banner</SelectItem>
                      </>
                    )}
                    {printCategory === 'step_repeat' && (
                      <>
                        <SelectItem value="step_repeat_8x8">8'×8' Step & Repeat</SelectItem>
                        <SelectItem value="step_repeat_8x9">8'×9' Step & Repeat</SelectItem>
                        <SelectItem value="step_repeat_8x10">8'×10' Step & Repeat</SelectItem>
                      </>
                    )}
                    {printCategory === 'pillowcase' && (
                      <>
                        <SelectItem value="pillowcase_8x8">8'×8' Pillowcase Backdrop</SelectItem>
                        <SelectItem value="pillowcase_8x10">8'×10' Pillowcase Backdrop</SelectItem>
                        <SelectItem value="pillowcase_8x20">8'×20' Pillowcase Backdrop</SelectItem>
                      </>
                    )}
                    {printCategory === 'canopies' && (
                      <>
                        <SelectItem value="event_tent_10x10">10×10 Event Tent</SelectItem>
                        <SelectItem value="event_tent_10x15">10×15 Event Tent</SelectItem>
                        <SelectItem value="event_tent_10x20">10×20 Event Tent</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Only show width/height for categories that need custom sizing (vinyl_banners only) */}
            {printCategory === 'vinyl_banners' && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Width (feet)</Label>
                    <Input
                      type="number"
                      min="0.5"
                      max="50"
                      step="0.5"
                      value={printWidth}
                      onChange={(e) => setPrintWidth(parseFloat(e.target.value) || 3.0)}
                      className="mt-1"
                      data-testid="print-width-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Height (feet)</Label>
                    <Input
                      type="number"
                      min="0.5"
                      max="50"
                      step="0.5"
                      value={printHeight}
                      onChange={(e) => setPrintHeight(parseFloat(e.target.value) || 2.0)}
                      className="mt-1"
                      data-testid="print-height-input"
                    />
                  </div>
                </div>

                <div className="text-sm text-gray-600 space-y-1">
                  <p>Print size: {printWidth}' × {printHeight}' = {(printWidth * printHeight).toFixed(2)} sq ft</p>
                  <p className="text-xs text-blue-600">
                    💡 Professional quality | Shipping: {(printWidth * printHeight) >= 240 ? '$50 (20\'+ banner)' : '$20 (standard)'}
                  </p>
                </div>
              </>
            )}

            {/* For fixed-size products, show the dimensions but no input fields */}
            {printCategory !== 'vinyl_banners' && (
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">
                  {printCategory === 'banner_stands' && 'Retractable banner stands include graphics and hardware'}
                  {printCategory === 'step_repeat' && 'Standard sizes available - perfect for events and photo ops'}
                  {printCategory === 'pillowcase' && 'Stretch fabric backdrops that slide over frame like a pillowcase'}
                  {printCategory === 'canopies' && 'Full 360° branding with custom graphics and aluminum frame'}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  💡 Fixed pricing | Shipping: $20 standard
                </p>
              </div>
            )}
          </div>
        )}

        {/* Quantity - Conditional Display Based on Product Type */}
        {!(productId === 'embroidery' && isNamesOnly) && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <Label className="text-lg font-semibold text-green-800">
              {productId === 'embroidery' ? 'Number of Garments to Embroider' : 'Quantity'}
            </Label>

            {/* Size Breakdown for Apparel */}
            {!['stickers', 'large_format', 'fastisol_transfers'].includes(productId) && (
              <div className="mt-3">
                <p className="text-sm text-green-700 mb-2">Enter quantity per size:</p>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {Object.keys(sizeBreakdown).map((size) => (
                    <div key={size} className="text-center">
                      <label className="text-xs font-bold text-gray-700 block mb-1">{size}</label>
                      <Input
                        type="number"
                        min="0"
                        max="5000"
                        value={sizeBreakdown[size] || ""}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setSizeBreakdown(prev => {
                            const updated = { ...prev, [size]: val };
                            const total = Object.values(updated).reduce((a, b) => a + b, 0);
                            setQuantity(Math.max(total, getMinimumQuantity(productId)));
                            return updated;
                          });
                        }}
                        onFocus={(e) => e.target.select()}
                        className="text-center text-sm font-bold h-10"
                        data-testid={`size-${size.toLowerCase()}`}
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-green-200">
                  <span className="text-sm font-semibold text-green-800">Total Pieces:</span>
                  <span className="text-lg font-bold text-green-900" data-testid="total-quantity">{quantity}</span>
                </div>
              </div>
            )}

            {/* Simple Quantity for non-apparel */}
            {['stickers', 'large_format', 'fastisol_transfers'].includes(productId) && (
              <>
                <Input
                  id="quantity"
                  type="number"
                  min={getMinimumQuantity(productId)}
                  max="10000"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || getMinimumQuantity(productId))}
                  onFocus={(e) => e.target.select()}
                  className="mt-2 text-lg font-bold"
                  data-testid="quantity-input"
                  placeholder={`Minimum: ${getMinimumQuantity(productId)}`}
                />
              </>
            )}

            <div className="mt-2 space-y-1">
              {getMinimumQuantity(productId) > 1 && (
                <p className="text-sm text-green-700">
                  Minimum quantity: {getMinimumQuantity(productId)}
                </p>
              )}
              {productId === 'embroidery' && (
                <p className="text-xs text-green-600">
                  Each garment will be embroidered with your design
                </p>
              )}
              {(() => {
                const priceBreakInfo = getPriceBreakInfo(productId, quantity);
                return priceBreakInfo && !dismissedNotifications[`priceBreak-${productId}`] && (
                  <div 
                    className="bg-blue-100 border border-blue-300 rounded p-2 mt-2 relative touch-pan-x"
                    onTouchStart={(e) => handleTouchStart(e, `priceBreak-${productId}`)}
                    onTouchMove={(e) => handleTouchMove(e)}
                    onTouchEnd={(e) => handleTouchEnd(e, `priceBreak-${productId}`)}
                  >
                    <button
                      onClick={() => dismissNotification(`priceBreak-${productId}`)}
                      className="absolute top-1 right-1 text-blue-600 hover:text-blue-800 w-5 h-5 flex items-center justify-center rounded-full hover:bg-blue-200"
                    >
                      ✕
                    </button>
                    <p className="text-sm text-blue-800 pr-6">
                      💡 <strong>{priceBreakInfo.away} more pieces</strong> to reach next price break ({priceBreakInfo.quantity}+ pieces)
                    </p>
                    <p className="text-xs text-blue-700">{priceBreakInfo.benefit}</p>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Names Only Quantity Display */}
        {productId === 'embroidery' && isNamesOnly && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <Label className="text-lg font-semibold text-purple-800">Number of Garments</Label>
            <Input
              type="number"
              min="1"
              max="10000"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              onFocus={(e) => e.target.select()}
              className="mt-2 text-lg font-bold"
              data-testid="names-quantity-input"
            />
            <p className="text-sm text-purple-700 mt-2">
              Each garment will be embroidered with the names you specify above
            </p>
            <div className="bg-purple-100 rounded p-2 mt-2">
              <p className="text-xs text-purple-800">
                <strong>Names pricing:</strong> ${numberOfNames * 15} for {numberOfNames} name{numberOfNames > 1 ? 's' : ''}<br />
                <strong>Garment pricing:</strong> Applied per garment for embroidery work
              </p>
            </div>
          </div>
        )}

        {/* Embroidery Type Selection */}
        {productId === 'embroidery' && (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-2">Embroidery Type</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <input
                    type="radio"
                    id="custom-design"
                    name="embroidery-type"
                    checked={!isNamesOnly}
                    onChange={() => setIsNamesOnly(false)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="custom-design" className="cursor-pointer text-sm">
                    <strong>Custom Design/Logo</strong> - Professional embroidery with your design
                  </Label>
                </div>
                <div className="flex items-center space-x-4">
                  <input
                    type="radio"
                    id="names-only"
                    name="embroidery-type"
                    checked={isNamesOnly}
                    onChange={() => setIsNamesOnly(true)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="names-only" className="cursor-pointer text-sm">
                    <strong>Names Only</strong> - $15 per name (perfect for personalization)
                  </Label>
                </div>
              </div>
            </div>

            {isNamesOnly && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-3">Names Configuration</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-blue-700">
                      <strong>$15 per name</strong> • Flat pricing for personalization
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setNumberOfNames(prev => prev + 1)}
                      className="text-xs px-3 py-1"
                    >
                      + Add Name
                    </Button>
                  </div>
                  
                  {/* Individual name input boxes */}
                  <div className="space-y-2">
                    {Array.from({ length: numberOfNames }, (_, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Label className="text-sm font-medium w-12">
                          Name {index + 1}:
                        </Label>
                        <Input
                          type="text"
                          placeholder={`Enter name ${index + 1}`}
                          value={namesList[index] || ''}
                          onChange={(e) => updateNamesList(index, e.target.value)}
                          className="flex-1"
                          data-testid={`name-input-${index}`}
                        />
                        {numberOfNames > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeName(index)}
                            className="text-red-600 hover:text-red-800 px-2"
                          >
                            ✕
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-green-100 border border-green-300 rounded p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-800">
                        <strong>{numberOfNames} name{numberOfNames > 1 ? 's' : ''}</strong> × $15 each
                      </span>
                      <span className="font-bold text-green-900">
                        Total: ${numberOfNames * 15}
                      </span>
                    </div>
                    <p className="text-xs text-green-700 mt-1">
                      Names will be embroidered on {quantity} garment{quantity > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!isNamesOnly && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Design Size</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Width (inches)</Label>
                    <Input
                      type="number"
                      min="0.5"
                      max="25"
                      step="0.25"
                      value={embroideryWidth}
                      onChange={(e) => setEmbroideryWidth(parseFloat(e.target.value) || 2.0)}
                      className="mt-1"
                      data-testid="embroidery-width-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Height (inches)</Label>
                    <Input
                      type="number"
                      min="0.5"
                      max="25"
                      step="0.25"
                      value={embroideryHeight}
                      onChange={(e) => setEmbroideryHeight(parseFloat(e.target.value) || 1.0)}
                      className="mt-1"
                      data-testid="embroidery-height-input"
                    />
                  </div>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Design size: {embroideryWidth}" × {embroideryHeight}" = {(embroideryWidth * embroideryHeight).toFixed(2)} sq in (~{Math.round((embroideryWidth * embroideryHeight) * 1000).toLocaleString()} stitches)</p>
                  <p className="text-xs text-purple-600">
                    💡 Over 4 sq inches (4,000+ stitches) = $15 digitizing fee
                  </p>
                  {(embroideryWidth * embroideryHeight) > 4 && (
                    <p className="text-orange-600 font-medium text-xs">
                      $15 digitizing fee will be added for this design size
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Moved quantity section higher - now appears after garment selection */}
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (!product) {
    return <div>Product not found</div>;
  }

  const needsLocations = productId === 'screen_printing';
  const isConfigured = !needsLocations || printLocations.length > 0;
  
  const isDTFReady = productId === 'dtf_printing' && garmentType;
  const showBuyButton = isConfigured || isDTFReady;
  const artFee = artFeeAccepted ? 25 * quantity : 0;

  // Calculate digitizing fee for embroidery
  let digitizingFee = 0;
  if (productId === 'embroidery') {
    if (isNamesOnly) {
      digitizingFee = 15;
    } else if ((embroideryWidth * embroideryHeight) > 4) {
      digitizingFee = 15;
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO
        title={product?.name || "Product Details"}
        description={product?.description || "Configure your custom print order with instant pricing."}
        path={`/product/${productId}`}
      />
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <img
            src={product.image}
            alt={product.name}
            className="w-full rounded-2xl shadow-lg"
          />
        </div>

        <div className="space-y-6">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
            <p className="text-xl text-gray-600 mb-6">
              {productId === 'fastisol_transfers' ? 
                "DIY heat transfer printing for home makers - apply transfers yourself!" : 
                product.description
              }
            </p>
            
            {/* Price Breakdown */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <div className="text-3xl font-bold text-orange-600 mb-2">
                {isNamesOnly && priceBreakdown.names_pricing ? (
                  <>
                    ${(priceBreakdown.names_pricing + priceBreakdown.total_garment_embroidery_cost).toFixed(2)}
                    {quantity > 1 && (
                      <span className="text-lg text-gray-500">
                        (${priceBreakdown.per_piece_cost.toFixed(2)} per piece + ${priceBreakdown.names_pricing.toFixed(2)} names)
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    ${price.toFixed(2)} 
                    {quantity > 1 && <span className="text-lg text-gray-500">(${(price/quantity).toFixed(2)} each)</span>}
                  </>
                )}
              </div>
              {isConfigured && price > 0 && (
                <div className="text-sm text-gray-600 space-y-1">
                  {/* Only show additional fees, not cost breakdown */}
                  {/* Show detailed breakdown for names-only embroidery */}
                  {isNamesOnly && priceBreakdown.names_pricing && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-2">
                      <h4 className="font-semibold text-purple-800 mb-2">Names-Only Pricing Breakdown</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Names ({priceBreakdown.number_of_names} × $15)</span>
                          <span>${priceBreakdown.names_pricing.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Garments & Embroidery ({quantity} pieces)</span>
                          <span>${priceBreakdown.total_garment_embroidery_cost.toFixed(2)}</span>
                        </div>
                        <div className="border-t border-purple-300 pt-1 flex justify-between font-semibold">
                          <span>Total</span>
                          <span>${(priceBreakdown.names_pricing + priceBreakdown.total_garment_embroidery_cost).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {artFee > 0 && (
                    <div className="text-orange-600 font-medium">Art Enhancement Fee: ${artFee.toFixed(2)}</div>
                  )}
                  
                  {printLocations.length > 1 && (
                    <div className="text-xs">Multiple locations: {printLocations.map(loc => PRINT_LOCATIONS[loc].name).join(', ')}</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {renderProductOptions()}

          {/* File Upload with Smart Analysis */}
          <div>
            <Label className="text-lg font-semibold">Upload Artwork - Smart Analysis Included</Label>
            <div className="space-y-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('fileInput').click()}
                className="w-full p-4 border-2 border-dashed border-gray-300 hover:border-gray-400"
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload Artwork
              </Button>
              
              <div className="text-center text-gray-500">or</div>
              
              <Link
                to={productId === "stickers"
                  ? `/design-studio?cat=stickers`
                  : `/design-studio?cat=apparel&method=${productId}`
                }
              >
                <Button
                  type="button"
                  className="w-full p-4 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white"
                  data-testid="open-design-studio-btn"
                >
                  <Paintbrush className="mr-2 h-4 w-4" />
                  Open Design Studio
                </Button>
              </Link>
              
              <input
                id="fileInput"
                type="file"
                onChange={handleFileUpload}
                accept="image/*,.pdf,.ai,.eps,.psd"
                className="hidden"
              />
            </div>
            
            {uploadedFile && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-green-700 font-medium">
                    {uploadedFile.original_name} uploaded successfully
                  </span>
                </div>
              </div>
            )}

            {/* Quality Analysis Results */}
            <ArtworkQualityAlert 
              qualityAnalysis={qualityAnalysis} 
              onAcceptFee={handleAcceptArtFee}
            />
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              size="lg"
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
              onClick={addToCart}
              disabled={!showBuyButton || (qualityAnalysis?.needs_art_fee && !artFeeAccepted)}
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag className="mr-2 w-5 h-5" />
              {isNamesOnly && priceBreakdown.names_pricing 
                ? `Buy Now - $${(priceBreakdown.names_pricing + priceBreakdown.total_garment_embroidery_cost).toFixed(2)}`
                : `Buy Now - $${price.toFixed(2)}`}
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              className="w-full"
              onClick={requestCall}
              disabled={!showBuyButton}
              data-testid="request-call-btn"
            >
              <Phone className="mr-2 w-5 h-5" />
              Request a Callback
            </Button>
            
            {(!showBuyButton || (qualityAnalysis?.needs_art_fee && !artFeeAccepted)) && (
              <p className="text-sm text-gray-500 text-center">
                {!showBuyButton && needsLocations && "Please select print locations"}
                {!showBuyButton && !needsLocations && productId !== 'dtf_printing' && "Please configure your options"}
                {!showBuyButton && productId === 'dtf_printing' && "Please select a garment"}
                {(qualityAnalysis?.needs_art_fee && !artFeeAccepted && uploadedFile) && " • Accept art fee above"}
              </p>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

// FAQ Page

export { ProductDetail };
export default ProductDetail;
